import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { AnchorHTMLAttributes, DetailedHTMLProps } from 'react';
import ReactMarkdown from 'react-markdown';
import { ReactMarkdownProps } from 'react-markdown/lib/complex-types';
import { PluggableList } from 'react-markdown/lib/react-markdown';
import { CardImageAspectRatio } from '@/helpers/determineAspectRatioByCardSpan';
import classNames from '@/lib/classNames';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { VideoJsonLd } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import rehypeAttrs from 'rehype-attr';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkUnwrapImages from 'remark-unwrap-images';
import Button from '../Button';
import { LinkRenderer } from '../Link';
import { ResponsiveImage } from '../ResponsiveImage';

const StarMuxVideo = dynamic(() => import('@/components/elements/StarMuxVideo'), {
    ssr: false,
});

/**
 * Rich Text component
 */
const RichText = ({ content, className }: { content: string; className?: string }) => {
    const router = useRouter();
    return (
        <ReactMarkdown
            className={classNames('prose mx-auto', className)}
            components={{
                img: ({ src = '', alt = '', ...rest }) => {
                    if (src?.includes('layout=plain')) {
                        // eslint-disable-next-line @next/next/no-img-element
                        return <img src={src} alt={alt} {...rest} />;
                    }
                    return (
                        <div className="rich-text-image">
                            <ResponsiveImage aspectRatio={CardImageAspectRatio.full} className="rounded-3xl not-prose" src={src} alt={alt} sizes={`(max-width: 1723px) 100vw, 1660px`} />
                        </div>
                    );
                },
                video: ({ src, title }) => {
                    const thumbnailUrl = `https://image.mux.com/${src}/thumbnail.webp?time=0`;
                    return (
                        <AspectRatio ratio={16 / 9} className="w-full not-prose">
                            <StarMuxVideo
                                className="w-full rounded-2xl"
                                playbackId={src}
                                placeholder={thumbnailUrl}
                                poster={thumbnailUrl}
                                controls
                                streamType="on-demand"
                                controlsList="playback"
                                metadata={{
                                    video_title: title ?? '',
                                }}
                            />
                            <VideoJsonLd name={title ?? ''} description={title ?? ''} thumbnailUrls={thumbnailUrl} contentUrl={`https://stream.mux.com/${src}/medium.mp4`} />
                        </AspectRatio>
                    );
                },
                // eslint-disable-next-line
                // @ts-ignore
                a: (
                    props: Omit<DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'ref'> &
                        ReactMarkdownProps & {
                            'data-config'?: string;
                        }
                ) => {
                    const { node, ...rest } = props;

                    if (props['data-config']) {
                        const dataAttrs = JSON.parse(props['data-config'] ?? '');

                        const buttonType = dataAttrs['data-button-type'];
                        const buttonText = `${props.children[0]}`;

                        if (buttonType) {
                            return (
                                <Button
                                    className="no-underline w-fit flex my-5"
                                    variant="outline"
                                    colour={buttonType === 'dark' ? 'primary' : 'white'}
                                    asChild
                                    onClick={() => {
                                        track(TRACKING_EVENT.PRESSED_BUTTON, {
                                            label: buttonText,
                                            page: router.asPath,
                                        });
                                    }}
                                >
                                    <LinkRenderer linkToExternalPage={props.href}>{buttonText}</LinkRenderer>
                                </Button>
                            );
                        }
                    }

                    return <a {...rest} />;
                },
            }}
            remarkPlugins={[remarkGfm, remarkUnwrapImages]}
            rehypePlugins={[rehypeRaw, [rehypeAttrs, { properties: 'string' }]] as PluggableList}
        >
            {content as string}
        </ReactMarkdown>
    );
};

export default RichText;
